import { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
  MarkerType,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import '@xyflow/react/dist/style.css';
import type { KnowledgeEdge, KnowledgeEntity, KnowledgeSnapshot } from '@/entities/knowledge';
import { useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';

const VISUAL_EDGE_TYPES = new Set(['IN_DISTRICT', 'RELATES_TO', 'AFFECTS']);
const NODE_WIDTH = 260;
const DOMAIN_NODE_WIDTH = 180;
const DOMAIN_NODE_HEIGHT = 56;
const NODE_LABEL_HEIGHT = 18;
const NODE_PAD_Y = 20;
const NODE_LINE_HEIGHT = 18;
const NODE_CHARS_PER_LINE = 30;

export type KnowledgeGraphSelection = {
  label: string;
  id: string;
  name: string;
};

type KnowledgeNodeData = {
  kind: 'entity' | 'domain';
  label: string;
  entityId: string;
  name: string;
  domain: string;
  selected: boolean;
};

function entityName(entity: KnowledgeEntity): string {
  const props = entity.props ?? {};
  const name = props.name ?? props.title ?? entity.id;
  return String(name);
}

function entityDomain(entity: KnowledgeEntity): string {
  return String(entity.domain || entity.topic || entity.props?.domain || '');
}

function domainNodeId(domain: string): string {
  return `domain:${domain}`;
}

function estimateNodeHeight(name: string): number {
  const lines = Math.max(1, Math.ceil(name.length / NODE_CHARS_PER_LINE));
  return NODE_PAD_Y + NODE_LABEL_HEIGHT + lines * NODE_LINE_HEIGHT;
}

function KnowledgeEntityNode({ data }: NodeProps) {
  const nodeData = data as KnowledgeNodeData;
  return (
    <div
      className={cn(
        'rounded-md border bg-card px-3 py-2 shadow-sm',
        nodeData.selected ? 'border-primary ring-2 ring-primary/30' : 'border-border',
      )}
      style={{ width: NODE_WIDTH }}
    >
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {nodeData.label}
        {nodeData.domain ? ` · ${nodeData.domain}` : ''}
      </div>
      <div className="text-sm font-medium leading-snug wrap-break-word whitespace-normal">
        {nodeData.name}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
}

function KnowledgeDomainNode({ data }: NodeProps) {
  const { t } = useI18n();
  const nodeData = data as KnowledgeNodeData;
  return (
    <div
      className={cn(
        'rounded-md border border-neutral-400 bg-white px-3 py-2 text-black shadow-sm',
        nodeData.selected ? 'ring-2 ring-black/35' : null,
      )}
      style={{ width: DOMAIN_NODE_WIDTH, minHeight: DOMAIN_NODE_HEIGHT }}
    >
      <Handle type="target" position={Position.Left} className="!bg-neutral-800" />
      <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-600">
        {t.common.domain}
      </div>
      <div className="text-sm font-semibold leading-snug">{nodeData.name}</div>
      <Handle type="source" position={Position.Right} className="!bg-neutral-800" />
    </div>
  );
}

const nodeTypes = { knowledge: KnowledgeEntityNode, domain: KnowledgeDomainNode };

function layoutElements(
  entities: KnowledgeEntity[],
  edges: KnowledgeEdge[],
  selectedKey: string | null,
): { nodes: Node[]; edges: Edge[] } {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: 'LR', nodesep: 36, ranksep: 72 });

  const byKey = new Map<string, KnowledgeEntity>();
  const heights = new Map<string, number>();
  const domains = new Set<string>();

  for (const entity of entities) {
    if ((entity.locale || 'en') === 'ru') continue;
    // Topic hubs are replaced by explicit domain nodes.
    if (entity.label === 'Topic') continue;
    const key = `${entity.label}:${entity.id}`;
    const height = estimateNodeHeight(entityName(entity));
    const domain = entityDomain(entity);
    byKey.set(key, entity);
    heights.set(key, height);
    graph.setNode(key, { width: NODE_WIDTH, height });
    if (domain) domains.add(domain);
  }

  for (const domain of domains) {
    graph.setNode(domainNodeId(domain), {
      width: DOMAIN_NODE_WIDTH,
      height: DOMAIN_NODE_HEIGHT,
    });
  }

  const flowEdges: Edge[] = [];
  for (const edge of edges) {
    if (!VISUAL_EDGE_TYPES.has(edge.type)) continue;
    const fromKey = `${edge.from_label}:${edge.from_id}`;
    const toKey = `${edge.to_label}:${edge.to_id}`;
    if (!byKey.has(fromKey) || !byKey.has(toKey)) continue;
    graph.setEdge(fromKey, toKey);
    flowEdges.push({
      id: `${fromKey}-${edge.type}-${toKey}`,
      source: fromKey,
      target: toKey,
      label: edge.type,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 16,
        height: 16,
        color: 'var(--foreground)',
      },
      style: { strokeWidth: 1.5, stroke: 'var(--foreground)', opacity: 0.55 },
      labelShowBg: true,
      labelBgPadding: [6, 3],
      labelBgBorderRadius: 4,
      labelBgStyle: {
        fill: 'var(--background)',
        fillOpacity: 1,
        stroke: 'var(--border)',
        strokeWidth: 1,
      },
      labelStyle: {
        fontSize: 11,
        fontWeight: 700,
        fill: 'var(--foreground)',
        letterSpacing: '0.02em',
      },
    });
  }

  for (const [key, entity] of byKey) {
    const domain = entityDomain(entity);
    if (!domain) continue;
    const domainKey = domainNodeId(domain);
    graph.setEdge(domainKey, key);
    flowEdges.push({
      id: `${domainKey}->${key}`,
      source: domainKey,
      target: key,
      style: { strokeWidth: 1.25, stroke: '#a3a3a3', opacity: 0.7 },
    });
  }

  dagre.layout(graph);

  const nodes: Node[] = [];
  for (const domain of [...domains].sort()) {
    const key = domainNodeId(domain);
    const position = graph.node(key);
    nodes.push({
      id: key,
      type: 'domain',
      position: {
        x: (position?.x ?? 0) - DOMAIN_NODE_WIDTH / 2,
        y: (position?.y ?? 0) - DOMAIN_NODE_HEIGHT / 2,
      },
      data: {
        kind: 'domain',
        label: 'Domain',
        entityId: domain,
        name: domain,
        domain,
        selected: key === selectedKey,
      } satisfies KnowledgeNodeData,
    });
  }

  for (const [key, entity] of byKey) {
    const position = graph.node(key);
    const height = heights.get(key) ?? estimateNodeHeight(entityName(entity));
    nodes.push({
      id: key,
      type: 'knowledge',
      position: {
        x: (position?.x ?? 0) - NODE_WIDTH / 2,
        y: (position?.y ?? 0) - height / 2,
      },
      data: {
        kind: 'entity',
        label: entity.label,
        entityId: entity.id,
        name: entityName(entity),
        domain: entityDomain(entity),
        selected: key === selectedKey,
      } satisfies KnowledgeNodeData,
    });
  }

  return { nodes, edges: flowEdges };
}

export function KnowledgeGraphCanvas({
  snapshot,
  selected,
  onSelect,
}: {
  snapshot: KnowledgeSnapshot | undefined;
  selected: KnowledgeGraphSelection | null;
  onSelect: (selection: KnowledgeGraphSelection | null) => void;
}) {
  const selectedKey = selected
    ? selected.label === 'Domain'
      ? domainNodeId(selected.id)
      : `${selected.label}:${selected.id}`
    : null;

  const { nodes, edges } = useMemo(() => {
    if (!snapshot) return { nodes: [] as Node[], edges: [] as Edge[] };
    const entities = (snapshot.entities ?? []).filter((entity) => (entity.locale || 'en') !== 'ru');
    const entityKeys = new Set(
      entities
        .filter((entity) => entity.label !== 'Topic')
        .map((entity) => `${entity.label}:${entity.id}`),
    );
    const graphEdges = (snapshot.edges ?? []).filter(
      (edge) =>
        entityKeys.has(`${edge.from_label}:${edge.from_id}`) &&
        entityKeys.has(`${edge.to_label}:${edge.to_id}`),
    );
    return layoutElements(entities, graphEdges, selectedKey);
  }, [snapshot, selectedKey]);

  const onNodeClick = useCallback(
    (_: unknown, node: Node) => {
      const data = node.data as KnowledgeNodeData;
      onSelect({ label: data.label, id: data.entityId, name: data.name });
    },
    [onSelect],
  );

  return (
    <div className="h-full min-h-0 w-full overflow-hidden bg-background">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onPaneClick={() => onSelect(null)}
        fitView
        minZoom={0.15}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={18} size={1} />
      </ReactFlow>
    </div>
  );
}
